import { Box, Breakpoint, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Link, generatePath, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Business_Profile_Enum, Measurement_Unit_Enum } from '@kheops/graphql';
import { formatPriceByBillingType, formatSuggestedRetailPrice } from '@kheops/utils';
import { RoutePaths } from '../../routes/AppRoutes';
import SearchUtils from '../../common/utils/search.utils';
import SizedImage from '../../common/components/SizedImage';
import FileUtils, { PhotoSize } from '../../common/utils/file.utils';
import { ellipsisStyle, linkStyle } from '../../common/utils/style.utils';
import useCurrentBreakpoint from '../../hooks/useCurrentBreakpoint';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import QualityLabels from '../../search/product/QualityLabels';
import SocialTags from '../../social-proof/SocialTags';
import { productImageSizeByBreakpoint } from '../../discovery/state';
import { getProductCardUrl } from '../../discovery/discovery.utils';
import { formatPackagingPriceIncludingDescriptor } from '../../common/utils/common.utils';
import UpdateBasketButton from '../../search/product/UpdateBasketButton';
import WishlistProductButton from '../../wishlist/WishlistProductButton';
import { ProductCardData } from './product-card.types';

export interface ProductHitCardProps {
  product: ProductCardData;
  displayCompany?: boolean;
  displayPackaging?: boolean;
  displayPackagingPrice?: boolean;
  displaySuggestedRetailPrice?: boolean;
  displayBasketButton?: boolean;
  displayWishlistButton?: boolean;
  layout?: 'grid' | 'responsive';
  productImageSizeByBreakPoint?: Record<Breakpoint, PhotoSize>;
  productBoxSx?: SxProps;
  parentBoxSx?: SxProps;
  onProductClick?: (product: ProductCardData) => void;
  actionButton?: React.ReactNode;
}

export default function ProductHitCard({
  product,
  displayCompany = false,
  displayPackaging = false,
  displayPackagingPrice = false,
  displaySuggestedRetailPrice = false,
  displayBasketButton = false,
  displayWishlistButton = false,
  layout = 'responsive',
  productImageSizeByBreakPoint = productImageSizeByBreakpoint,
  productBoxSx,
  parentBoxSx,
  onProductClick,
  actionButton,
}: ProductHitCardProps): React.JSX.Element {
  const { pathname: origin } = useLocation();
  const { t } = useTranslation(['common', 'contracts', 'price']);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isListLayout = layout === 'responsive' && isXs;
  const currencyFormat = useCurrencyFormat();
  const breakpoint = useCurrentBreakpoint();
  const packagingUrl = getProductCardUrl(product);

  const productImage = (
    <SizedImage
      src={FileUtils.BuildPackagingImageUrl({ photo: product.photo, type: product.family, size: { ...productImageSizeByBreakPoint[breakpoint], fit: 'contain' } })}
      alt={t('common:product_one')}
      height={productImageSizeByBreakPoint[breakpoint].height}
      width={productImageSizeByBreakPoint[breakpoint].width!}
      sx={{
        aspectRatio: '4/3',
        borderRadius: 0,
        '& .img': {
          transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeOut,
          }),
        },
        '&:hover .img': {
          transform: 'scale(1.08)',
        },
      }}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: isListLayout ? 1 : 0,
        backgroundColor: 'surfaceContainerLowest.main',
        borderRadius: isListLayout ? 3 : 4,
        position: 'relative',
        boxShadow: '0 4px 15px 0 rgba(0, 0, 0, 0.03);',
        overflow: 'hidden',
        px: isListLayout ? 1.5 : 0,
        py: isListLayout ? 1 : 0,
        transition: theme.transitions.create('box-shadow', {
          duration: theme.transitions.duration.standard,
          easing: theme.transitions.easing.easeOut,
        }),
        '&:hover': {
          boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.08)',
        },
        ...parentBoxSx,
      }}
    >
      {isListLayout && (
        onProductClick ? (
          <Box
            onClick={() => onProductClick(product)}
            aria-label={product.name}
            sx={{ position: 'absolute', inset: 0, zIndex: 2, cursor: 'pointer' }}
          />
        ) : (
          <Box
            component={Link}
            to={packagingUrl}
            state={{ origin }}
            aria-label={product.name}
            sx={{ position: 'absolute', inset: 0, zIndex: 2 }}
          />
        )
      )}
      
      {!isListLayout && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 5,
          }}
        >
          <QualityLabels
            labels={product.labels}
            nbToDisplay={2}
            size={32}
            iconSx={{
              height: 32,
              width: 32,
              textAlign: 'center',
            }}
          />
        </Box>
      )}
      
      {isListLayout && displayBasketButton && (
        <Box sx={{ position: 'absolute', bottom: 8, right: 12, zIndex: 5 }}>
          <UpdateBasketButton packagingId={product.packagingId} supplyingCompanyId={product.companyId} />
        </Box>
      )}
      {!!product.socialTags.length && (
        <Box
          sx={{
            position: isListLayout ? 'initial' : 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            flexDirection: isListLayout ? 'row' : 'column',
            gap: 0.5,
            zIndex: 6,
          }}
        >
          <SocialTags tags={product.socialTags} displayRules={{ rules: { 1: 1, 2: 2 } }} />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isListLayout ? 'row-reverse' : 'column',
          gap: isListLayout ? 1 : 0,
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {onProductClick ? (
            <Box onClick={() => onProductClick(product)} sx={{ display: 'block', cursor: 'pointer' }}>
              {productImage}
            </Box>
          ) : (
            <Box component={Link} to={isXs ? '' : packagingUrl} state={{ origin }} sx={{ display: 'block' }}>
              {productImage}
            </Box>
          )}
          {displayWishlistButton && (
            <Box sx={{ position: 'absolute', bottom: 12, right: 12, zIndex: 5 }}>
              <WishlistProductButton
                supplierId={product.companyId}
                productSku={product.productSku}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            p: isListLayout ? 0 : 2,
            ml: isListLayout ? 0.5 : 0,
            ...productBoxSx,
          }}
        >
          {onProductClick ? (
            <Box onClick={() => onProductClick(product)} sx={{ mb: 0.5, ...linkStyle, display: 'block', cursor: 'pointer' }}>
              <Typography variant="titleSmall" sx={{ ...ellipsisStyle }}>
                {product.name}
              </Typography>
            </Box>
          ) : (
            <Box component={Link} to={packagingUrl} sx={{ mb: 0.5, ...linkStyle, display: 'block' }}>
              <Typography variant="titleSmall" sx={{ ...ellipsisStyle }}>
                {product.name}
              </Typography>
            </Box>
          )}
          
          <Typography variant="bodySmall" color="onSurfaceVariant">
            {product.baseUnitLabel}
            {displayPackaging && product.packagingLabel && ` - ${product.packagingLabel}`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isListLayout ? 'row' : 'column',
                alignItems: isListLayout ? 'center' : 'start',
                gap: isListLayout ? 0.5 : 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="titleMedium">
                  {SearchUtils.getFormatedUnitPrice(product, currencyFormat.format)}
                </Typography>
                {displaySuggestedRetailPrice && product.suggestedRetailPrice && (
                  <Typography variant="bodySmall" color="onSurfaceVariant">
                    {t('price:suggest_retail_price_acronym')} : {
                      formatSuggestedRetailPrice({
                        suggestedRetailPrice: product.suggestedRetailPrice,
                        billingType: product.billingType,
                        unitType: product.baseUnitType,
                        netContent: (product.baseUnitNetContent || product.packagingNetContent) as number,
                        measurementUnit: (product.baseUnitMeasurementUnit || product.packagingMeasurementUnit) as Measurement_Unit_Enum,
                      })
                    }
                  </Typography>
                )}
              </Box>
              {displayPackagingPrice && (
                <Typography variant="bodySmall" color="onSurfaceVariant">
                  {formatPackagingPriceIncludingDescriptor(t, formatPriceByBillingType(currencyFormat.format(Number(product.price)), product.billingType), product.packagingTradeItemUnitDescriptor)}
                </Typography>
              )}
            </Box>
            {!isListLayout && displayBasketButton && (
              <UpdateBasketButton packagingId={product.packagingId} supplyingCompanyId={product.companyId} />
            )}
          </Box>

          {isListLayout && !!product.labels.length && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
              <QualityLabels
                labels={product.labels}
                nbToDisplay={2}
                size={24}
                iconSx={{
                  height: 24,
                  width: 24,
                  textAlign: 'center',
                }}
              />
            </Box>
          )}

          {displayCompany && (
            <Box
              component={Link}
              to={generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: product.companyId })}
              sx={{
                maxWidth: '100%',
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 1,
                mt: isListLayout ? 1 : 1.5,
                color: 'onSurface.main',
                position: {
                  xs: 'relative',
                  sm: 'static',
                },
                zIndex: {
                  xs: 3,
                  sm: 'auto',
                },
                pointerEvents: {
                  xs: 'auto',
                },
              }}
            >
              <Box
                component="img"
                src={FileUtils.BuildCompanyImageUrl({ photo: product.companyPhoto, type: Business_Profile_Enum.Supplier, size: { height: 32, fit: 'contain' } })}
                sx={{
                  height: 32,
                  width: 32,
                  borderRadius: '50%',
                }}
                alt={t('common:company')}
              />
              <Typography variant="bodySmall" sx={ellipsisStyle}>
                {product.company}
              </Typography>
            </Box>
          )}
          {actionButton && (
            <Box sx={{ width: '100%', pt: 2, pb: 1 }}>
              {actionButton}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
